import { useRef, useState } from "react";
import { supabase } from "../supabaseClient";

const countyOptions = [
  "Albany",
  "Allegany",
  "Bronx",
  "Broome",
  "Cattaraugus",
  "Cayuga",
  "Chautauqua",
  "Chemung",
  "Chenango",
  "Clinton",
  "Columbia",
  "Cortland",
  "Delaware",
  "Dutchess",
  "Erie",
  "Essex",
  "Franklin",
  "Fulton",
  "Genesee",
  "Greene",
  "Hamilton",
  "Herkimer",
  "Jefferson",
  "Kings",
  "Lewis",
  "Livingston",
  "Madison",
  "Monroe",
  "Montgomery",
  "Nassau",
  "New York",
  "Niagara",
  "Oneida",
  "Onondaga",
  "Ontario",
  "Orange",
  "Orleans",
  "Oswego",
  "Otsego",
  "Putnam",
  "Queens",
  "Rensselaer",
  "Richmond",
  "Rockland",
  "Saratoga",
  "Schenectady",
  "Schoharie",
  "Schuyler",
  "Seneca",
  "St. Lawrence",
  "Steuben",
  "Suffolk",
  "Sullivan",
  "Tioga",
  "Tompkins",
  "Ulster",
  "Warren",
  "Washington",
  "Wayne",
  "Westchester",
  "Wyoming",
  "Yates",
  "Other",
];

const agencyOptions = [
  "AIM",
  "HIFI",
  "MNY",
  "Hamaspik",
  "Hamaspik Kings",
  "Hamaspik Rockland",
  "People Inc.",
  "Rayim of Hudson Valley",
  "A Very Special Place",
  "ACDS",
  "Catholic Charities of Rochester",
  "DDI",
  "JM Murray",
  "Community Mainstreaming Associates",
  "Metropolitan Development Center",
  "Children at Play",
  "Janus VII",
  "Ahavim",
  "NYSARC NYC",
  "AccessCNY",
  "Access Supports for Living",
  "Hawthorne Foundation",
  "Liberty Resources",
  "Franziska Racker Centers",
  "A-1 Universal Care",
  "IKAN",
  "Monroe County ARC",
  "Heritage Christian Services",
  "Springbrook Community Connections",
  "ARISE/EFR",
  "Other",
];

const certificationOptions = [
  "CPR",
  "First Aid",
  "SCIP-R",
  "CPI",
  "RBT",
  "DSP Training",
  "Medication Administration",
  "Mandated Reporter",
  "Behavior Support Training",
  "ABA Experience",
  "PCA Experience",
  "HHA Experience",
  "CNA",
  "Other",
];

const specializationOptions = [
  "Autism",
  "ADHD",
  "Behavioral Support",
  "Sensory Needs",
  "Nonverbal Support",
  "Community Integration",
  "Life Skills",
  "Social Skills",
  "Toileting Support",
  "Feeding Support",
  "Hygiene Support",
  "AAC / Communication Devices",
  "Mobility Support",
  "Seizure Support",
  "Overnight Support",
  "Children",
  "Teens",
  "Adults",
  "Other",
];

const languageOptions = [
  "English",
  "Spanish",
  "ASL",
  "French",
  "Russian",
  "Creole",
  "Arabic",
  "Other",
];

const serviceOptions = ["ComHab", "Respite"];
const ageGroupOptions = ["Children", "Teens", "Adults"];
const schedulePreferenceOptions = [
  "Weekday Mornings",
  "Weekday Afternoons",
  "Weekday Evenings",
  "Saturday",
  "Sunday",
  "Weekends",
  "Overnights",
  "School Breaks",
  "Summer Availability",
];

const transportationOptions = [
  "Has reliable personal transportation",
  "Relies on public transportation",
  "No transportation available",
];

const MIN_SUBMIT_SECONDS = 5;

const blockedKeywords = [
  "crypto",
  "bitcoin",
  "forex",
  "nft",
  "casino",
  "gambling",
  "loan",
  "seo",
  "backlink",
  "marketing agency",
  "escort",
  "porn",
  "sex",
  "viagra",
  "onlyfans",
  "telegram",
  "whatsapp",
  "dm for promotion",
  "make money fast",
  "click here",
  "adult service",
];

const profanityTerms = [
  "fuck",
  "fucking",
  "shit",
  "bitch",
  "asshole",
  "bastard",
  "cunt",
  "slut",
  "dick",
  "pussy",
  "nigger",
  "faggot",
  "retard",
];

function MultiSelectCheckboxList({
  label,
  options,
  selectedValues,
  onChange,
  height = "180px",
}) {
  const wrapperStyle = { marginBottom: "18px" };
  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#2f6f89",
  };
  const summaryStyle = {
    fontSize: "0.92rem",
    color: "#5b6470",
    marginBottom: "8px",
  };
  const listStyle = {
    border: "1px solid #d0d7de",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    padding: "12px",
    maxHeight: height,
    overflowY: "auto",
  };
  const itemStyle = {
    display: "block",
    marginBottom: "10px",
    fontSize: "0.98rem",
  };

  return (
    <div style={wrapperStyle}>
      <label style={labelStyle}>{label}</label>
      <div style={summaryStyle}>
        {selectedValues.length > 0
          ? `${selectedValues.length} selected`
          : "None selected"}
      </div>
      <div style={listStyle}>
        {options.map((option) => (
          <label key={option} style={itemStyle}>
            <input
              type="checkbox"
              value={option}
              checked={selectedValues.includes(option)}
              onChange={onChange}
              style={{ marginRight: "10px" }}
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  );
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function validatePhone(phone) {
  const trimmed = String(phone || "").trim();

  if (!trimmed) {
    return { valid: true, normalized: null };
  }

  const digits = trimmed.replace(/\D/g, "");

  if (digits.length !== 10) {
    return {
      valid: false,
      normalized: null,
      message: "Please enter a valid 10-digit phone number or leave it blank.",
    };
  }

  return {
    valid: true,
    normalized: digits,
  };
}

function validateEmail(email) {
  const trimmed = String(email || "").trim().toLowerCase();

  if (!trimmed) {
    return { valid: false, message: "Please enter your email address." };
  }

  if (!trimmed.includes("@")) {
    return { valid: false, message: "Improper email address: missing @ symbol." };
  }

  if (trimmed.includes(" ")) {
    return { valid: false, message: "Improper email address: spaces are not allowed." };
  }

  const parts = trimmed.split("@");

  if (parts.length !== 2) {
    return { valid: false, message: "Improper email address: it must contain only one @ symbol." };
  }

  const [localPart, domainPart] = parts;

  if (!localPart) {
    return { valid: false, message: "Improper email address: missing text before @." };
  }

  if (!domainPart) {
    return { valid: false, message: "Improper email address: missing domain after @." };
  }

  if (!domainPart.includes(".")) {
    return { valid: false, message: "Improper email address: domain must include a period." };
  }

  if (domainPart.startsWith(".") || domainPart.endsWith(".")) {
    return { valid: false, message: "Improper email address: domain cannot start or end with a period." };
  }

  if (trimmed.startsWith(".") || trimmed.endsWith(".")) {
    return { valid: false, message: "Improper email address: email cannot start or end with a period." };
  }

  return { valid: true, normalized: trimmed };
}

function findBlockedWordInText(text) {
  const normalized = normalizeText(text);
  const allBlockedTerms = [...blockedKeywords, ...profanityTerms];

  for (const term of allBlockedTerms) {
    if (normalized.includes(term)) {
      return term;
    }
  }

  return null;
}

function replaceOtherWithTypedValue(values, otherValue) {
  let finalValues = Array.isArray(values) ? [...values] : [];
  const trimmedOther = String(otherValue || "").trim();

  if (finalValues.includes("Other")) {
    finalValues = finalValues.filter((item) => item !== "Other");

    if (trimmedOther !== "") {
      finalValues.push(trimmedOther);
    }
  }

  return finalValues;
}

function findBlockedContentLocation(formData) {
  const fieldsToCheck = [
    { label: "First name", value: formData.first_name },
    { label: "Last name", value: formData.last_name },
    { label: "Email address", value: formData.email },
    { label: "Phone number", value: formData.phone_number },
    { label: "Other county/region", value: formData.other_county },
    { label: "Other agency", value: formData.other_agency },
    { label: "Other certification", value: formData.other_certification },
    { label: "Other specialization", value: formData.other_specialization },
    { label: "Other language", value: formData.other_language },
    { label: "Counties", value: formData.county_operating.join(" ") },
    { label: "Agencies onboarded with", value: formData.agencies_onboarded_with.join(" ") },
    { label: "Certifications", value: formData.certifications.join(" ") },
    { label: "Specializations", value: formData.specializations.join(" ") },
    { label: "Schedule preferences", value: formData.schedule_preferences.join(" ") },
    { label: "Age groups served", value: formData.age_groups_served.join(" ") },
    { label: "Languages spoken", value: formData.languages_spoken.join(" ") },
  ];

  for (const field of fieldsToCheck) {
    const matchedWord = findBlockedWordInText(field.value);
    if (matchedWord) {
      return `${field.label} contains the flagged word "${matchedWord}". Please remove it and try again.`;
    }
  }

  return null;
}

export default function JoinDSPNetwork() {
  const loadTimeRef = useRef(Date.now());

  const [formData, setFormData] = useState({
    services: [],
    first_name: "",
    last_name: "",
    age: "",
    county_operating: [],
    other_county: "",
    transportation_available: "",
    years_of_experience: "",
    agencies_onboarded_with: [],
    other_agency: "",
    certifications: [],
    other_certification: "",
    specializations: [],
    other_specialization: "",
    schedule_preferences: [],
    age_groups_served: [],
    languages_spoken: [],
    other_language: "",
    email: "",
    phone_number: "",
    website: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  const containerStyle = {
    padding: "60px 20px",
    maxWidth: "950px",
    margin: "0 auto",
  };

  const formCardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "18px",
    padding: "32px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
  };

  const inputStyle = {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "10px",
    border: "1px solid #d0d7de",
    fontSize: "1rem",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    color: "#2f6f89",
  };

  const checkboxGroupStyle = {
    marginBottom: "18px",
    padding: "14px",
    border: "1px solid #d0d7de",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
  };

  const checkboxItemStyle = {
    display: "block",
    marginBottom: "10px",
    fontSize: "0.98rem",
  };

  const buttonStyle = {
    width: "100%",
    padding: "16px",
    backgroundColor: "#2f6f89",
    color: "#ffffff",
    border: "none",
    borderRadius: "10px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.7 : 1,
  };

  const successBoxStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "35px",
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    textAlign: "center",
  };

  const noteStyle = {
    backgroundColor: "#f4f9fc",
    border: "1px solid #d8ebf3",
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "20px",
    color: "#2f6f89",
    fontSize: "0.96rem",
  };

  const hiddenFieldStyle = {
    position: "absolute",
    left: "-9999px",
    width: "1px",
    height: "1px",
    overflow: "hidden",
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleCheckboxArrayChange(e, fieldName) {
    const { value, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: checked
        ? [...prev[fieldName], value]
        : prev[fieldName].filter((item) => item !== value),
    }));
  }

  function buildWorkerPayload() {
    const finalCounties = replaceOtherWithTypedValue(
      formData.county_operating,
      formData.other_county
    );

    const finalAgencies = replaceOtherWithTypedValue(
      formData.agencies_onboarded_with,
      formData.other_agency
    );

    const finalCertifications = replaceOtherWithTypedValue(
      formData.certifications,
      formData.other_certification
    );

    const finalSpecializations = replaceOtherWithTypedValue(
      formData.specializations,
      formData.other_specialization
    );

    const finalLanguages = replaceOtherWithTypedValue(
      formData.languages_spoken,
      formData.other_language
    );

    const phoneValidation = validatePhone(formData.phone_number);
    const emailValidation = validateEmail(formData.email);

    return {
      first_name: (formData.first_name || "").trim(),
      last_name: (formData.last_name || "").trim(),
      email: emailValidation.normalized,
      phone_number: phoneValidation.normalized,
      age: formData.age ? Number(formData.age) : null,
      county_operating: finalCounties,
      transportation_available: (formData.transportation_available || "").trim(),
      years_of_experience:
        formData.years_of_experience === ""
          ? null
          : Math.max(0, Number(formData.years_of_experience)),
      agencies_onboarded_with: finalAgencies,
      certifications: finalCertifications,
      specializations: finalSpecializations,
      schedule_preferences: formData.schedule_preferences.join(", "),
      age_groups_served: formData.age_groups_served.join(", "),
      languages_spoken: finalLanguages,
      approved: false,
    };
  }

  async function upsertWorkerProfile(table, payload) {
    const { data: existingByEmail, error: existingError } = await supabase
      .from(table)
      .select("id")
      .eq("email", payload.email)
      .maybeSingle();

    if (existingError) {
      throw new Error(existingError.message);
    }

    if (existingByEmail) {
      const { error: updateError } = await supabase
        .from(table)
        .update({
          ...payload,
          approved: false,
        })
        .eq("id", existingByEmail.id);

      if (updateError) {
        throw new Error(updateError.message);
      }

      return;
    }

    const { error: insertError } = await supabase
      .from(table)
      .insert([
        {
          ...payload,
          approved: false,
        },
      ]);

    if (insertError) {
      throw new Error(insertError.message);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const elapsedSeconds = (Date.now() - loadTimeRef.current) / 1000;
      if (elapsedSeconds < MIN_SUBMIT_SECONDS) {
        throw new Error("Submission was too fast. Please review the form and try again.");
      }

      if ((formData.website || "").trim() !== "") {
        throw new Error("Submission could not be completed.");
      }

      if (formData.services.length === 0) {
        throw new Error("Please select at least one service type.");
      }

      if (formData.county_operating.length === 0) {
        throw new Error("Please select at least one county.");
      }

      if (formData.age_groups_served.length === 0) {
        throw new Error("Please select at least one age group served.");
      }

      if (!formData.transportation_available) {
        throw new Error("Please select a transportation option.");
      }

      if (
        formData.county_operating.includes("Other") &&
        !String(formData.other_county || "").trim()
      ) {
        throw new Error("Please type the other county/region.");
      }

      if (
        formData.agencies_onboarded_with.includes("Other") &&
        !String(formData.other_agency || "").trim()
      ) {
        throw new Error("Please type the other agency.");
      }

      if (
        formData.certifications.includes("Other") &&
        !String(formData.other_certification || "").trim()
      ) {
        throw new Error("Please type the other certification.");
      }

      if (
        formData.specializations.includes("Other") &&
        !String(formData.other_specialization || "").trim()
      ) {
        throw new Error("Please type the other specialization.");
      }

      if (
        formData.languages_spoken.includes("Other") &&
        !String(formData.other_language || "").trim()
      ) {
        throw new Error("Please type the other language.");
      }

      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message);
      }

      const phoneValidation = validatePhone(formData.phone_number);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.message);
      }

      const blockedLocationMessage = findBlockedContentLocation(formData);
      if (blockedLocationMessage) {
        throw new Error(blockedLocationMessage);
      }

      const payload = buildWorkerPayload();
      const operations = [];

      if (formData.services.includes("ComHab")) {
        operations.push(upsertWorkerProfile("comhab_workers", payload));
      }

      if (formData.services.includes("Respite")) {
        operations.push(upsertWorkerProfile("respite_workers", payload));
      }

      await Promise.all(operations);

      setSubmitted(true);
      setFormData({
        services: [],
        first_name: "",
        last_name: "",
        age: "",
        county_operating: [],
        other_county: "",
        transportation_available: "",
        years_of_experience: "",
        agencies_onboarded_with: [],
        other_agency: "",
        certifications: [],
        other_certification: "",
        specializations: [],
        other_specialization: "",
        schedule_preferences: [],
        age_groups_served: [],
        languages_spoken: [],
        other_language: "",
        email: "",
        phone_number: "",
        website: "",
      });

      loadTimeRef.current = Date.now();
    } catch (err) {
      setMessage(err.message || "There was an error submitting your DSP profile.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <section style={containerStyle}>
        <div style={successBoxStyle}>
          <h1 style={{ color: "#2f6f89", marginBottom: "15px" }}>
            Submission Received
          </h1>
          <p style={{ fontSize: "1.05rem", marginBottom: "12px" }}>
            Your DSP profile has been submitted successfully.
          </p>
          <p style={{ fontSize: "0.98rem", margin: 0, color: "#5b6470" }}>
            Your profile is now pending review and will remain hidden until approved.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section style={containerStyle}>
      <h1 style={{ fontSize: "2.2rem", color: "#2f6f89", marginBottom: "10px" }}>
        Join DSP Network
      </h1>

      <p style={{ fontSize: "1.05rem", marginBottom: "30px", maxWidth: "760px" }}>
        Complete the form below to join or update your Hopeful Horizons DSP profile for
        Community Habilitation and/or Respite services.
      </p>

      <div style={formCardStyle}>
        <div style={noteStyle}>
          Submitted DSP profiles are reviewed before being made visible on the website.
        </div>

        <form onSubmit={handleSubmit}>
          <div style={hiddenFieldStyle} aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <label style={labelStyle}>Service Type</label>
          <div style={checkboxGroupStyle}>
            {serviceOptions.map((item) => (
              <label key={item} style={checkboxItemStyle}>
                <input
                  type="checkbox"
                  value={item}
                  checked={formData.services.includes(item)}
                  onChange={(e) => handleCheckboxArrayChange(e, "services")}
                  style={{ marginRight: "10px" }}
                />
                {item}
              </label>
            ))}
          </div>

          <label style={labelStyle}>First Name</label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            style={inputStyle}
          />

          <label style={labelStyle}>Last Name</label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            style={inputStyle}
          />

          <label style={labelStyle}>Age</label>
          <input
            type="number"
            name="age"
            min="18"
            value={formData.age}
            onChange={handleChange}
            required
            style={inputStyle}
          />

          <MultiSelectCheckboxList
            label="County/Region(s) Operating"
            options={countyOptions}
            selectedValues={formData.county_operating}
            onChange={(e) => handleCheckboxArrayChange(e, "county_operating")}
            height="220px"
          />

          {formData.county_operating.includes("Other") && (
            <>
              <label style={labelStyle}>Other County/Region</label>
              <input
                type="text"
                name="other_county"
                value={formData.other_county}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Type the county or region"
              />
            </>
          )}

          <label style={labelStyle}>Transportation Availability</label>
          <select
            name="transportation_available"
            value={formData.transportation_available}
            onChange={handleChange}
            required
            style={inputStyle}
          >
            <option value="">Select an option</option>
            {transportationOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <label style={labelStyle}>Years of Experience</label>
          <input
            type="number"
            name="years_of_experience"
            min="0"
            value={formData.years_of_experience}
            onChange={handleChange}
            required
            style={inputStyle}
          />

          <MultiSelectCheckboxList
            label="Agencies Onboarded With"
            options={agencyOptions}
            selectedValues={formData.agencies_onboarded_with}
            onChange={(e) =>
              handleCheckboxArrayChange(e, "agencies_onboarded_with")
            }
            height="220px"
          />

          {formData.agencies_onboarded_with.includes("Other") && (
            <>
              <label style={labelStyle}>Other Agency</label>
              <input
                type="text"
                name="other_agency"
                value={formData.other_agency}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Type the agency name"
              />
            </>
          )}

          <MultiSelectCheckboxList
            label="Certifications"
            options={certificationOptions}
            selectedValues={formData.certifications}
            onChange={(e) => handleCheckboxArrayChange(e, "certifications")}
            height="180px"
          />

          {formData.certifications.includes("Other") && (
            <>
              <label style={labelStyle}>Other Certification</label>
              <input
                type="text"
                name="other_certification"
                value={formData.other_certification}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Type the certification"
              />
            </>
          )}

          <MultiSelectCheckboxList
            label="Specializations"
            options={specializationOptions}
            selectedValues={formData.specializations}
            onChange={(e) => handleCheckboxArrayChange(e, "specializations")}
            height="220px"
          />

          {formData.specializations.includes("Other") && (
            <>
              <label style={labelStyle}>Other Specialization</label>
              <input
                type="text"
                name="other_specialization"
                value={formData.other_specialization}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Type the specialization"
              />
            </>
          )}

          <MultiSelectCheckboxList
            label="Schedule Preferences"
            options={schedulePreferenceOptions}
            selectedValues={formData.schedule_preferences}
            onChange={(e) =>
              handleCheckboxArrayChange(e, "schedule_preferences")
            }
            height="180px"
          />

          <MultiSelectCheckboxList
            label="Age Groups Served"
            options={ageGroupOptions}
            selectedValues={formData.age_groups_served}
            onChange={(e) => handleCheckboxArrayChange(e, "age_groups_served")}
            height="120px"
          />

          <MultiSelectCheckboxList
            label="Languages Spoken"
            options={languageOptions}
            selectedValues={formData.languages_spoken}
            onChange={(e) => handleCheckboxArrayChange(e, "languages_spoken")}
            height="180px"
          />

          {formData.languages_spoken.includes("Other") && (
            <>
              <label style={labelStyle}>Other Language</label>
              <input
                type="text"
                name="other_language"
                value={formData.other_language}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Type the language"
              />
            </>
          )}

          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            style={inputStyle}
          />

          <label style={labelStyle}>Phone Number (Optional)</label>
          <input
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Enter 10-digit phone number"
          />

          {message && (
            <div
              style={{
                backgroundColor: "#fff3f3",
                border: "1px solid #f0c2c2",
                color: "#a33a3a",
                borderRadius: "10px",
                padding: "12px",
                marginBottom: "14px",
              }}
            >
              {message}
            </div>
          )}

          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? "Submitting..." : "Submit DSP Profile"}
          </button>
        </form>
      </div>
    </section>
  );
}